import { ApiResponseError, Currency, InfluencerTag } from '@round/api';
import { FormikErrors } from 'formik';
import { GenericDropdownOption } from './utils.types';

/**
 * Parsing search params from `location.search`
 * to an object.
 * @param searchParams
 */
export function parseUrlSearchParams(searchParams: string) {
    const params = new URLSearchParams(searchParams);
    return Array.from(params.entries())
        .filter((entry) => !!entry[1])
        .reduce<Record<string, string>>((acc, value) => {
            acc[value[0]] = value[1];
            return acc;
        }, {});
}

export function formatDate(asString: string, options: Intl.DateTimeFormatOptions = {}) {
    // 20 Jan 2020
    const parsed = new Date(asString);
    return parsed.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...options,
    });
}

export function formatNumberToCommaNotation(num: number | string) {
    return String(num).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export function asMoney(value: number | undefined | null, currency: Currency | undefined, fractionDigits = 2) {
    if (typeof value !== 'number' || isNaN(value) || value === Infinity) {
        return '-';
    }

    return `${currency?.symbol ?? ''}${formatNumberToCommaNotation(value.toFixed(fractionDigits))}`;
}

export function formatYesNo(bool: boolean) {
    return bool ? 'Yes' : 'No';
}

export function mapApiErrorsToFormikErrors<T extends Record<string, any>>(
    apiErrors: ApiResponseError<T>
): FormikErrors<T> {
    return Object.keys(apiErrors).reduce((formikErrors, key) => {
        const value = apiErrors[key as keyof ApiResponseError<T>];
        return {
            ...formikErrors,
            [key]: typeof value === 'string' ? value : value?.join(''),
        };
    }, {} as FormikErrors<T>);
}

export type TagOption = GenericDropdownOption<number> & { type: InfluencerTag['type']; code: InfluencerTag['code'] };
export function mapInfluencerTagsToOptions(influencerTags: InfluencerTag[]) {
    const tagOptions: TagOption[] = influencerTags.map((tag) => ({
        value: tag.id,
        label: tag.name,
        type: tag.type,
        code: tag.code,
    }));
    const locationTagOptions = tagOptions.filter((tag) => tag.type === 'LOCATION');
    const categoryTagOptions = tagOptions.filter((tag) => tag.type === 'CATEGORY');

    return {
        tagOptions,
        locationTagOptions,
        categoryTagOptions,
    };
}

export const buildInstagramUserUrl = (username: string) => {
    return `https://instagram.com/${username}`;
};

export const buildTiktokUserUrl = (uniqueId: string) => {
    return `https://www.tiktok.com/@${uniqueId}`;
};

export const buildYoutubeUserUrl = (username: string) => {
    return `https://www.youtube.com/@${username}`;
};

/**
 * Process response and download file
 * @param response
 * @param mimeType - optional param. If not passed content-type from response is used
 */
export async function downloadFileFromResponse(response: Response, mimeType?: string) {
    const FILE_NAME_REGEX = /filename="(.*)"/i;
    const contentDisposition = response.headers.get('content-disposition');
    const fileName = contentDisposition?.match(FILE_NAME_REGEX)?.[1] || '';

    const buffer = await response.arrayBuffer();
    const responseContentType = response.headers.get('content-type') || undefined;
    const blob = new Blob([buffer], { type: mimeType ?? responseContentType });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.setAttribute('download', fileName);
    a.click();
}

export function formatToIsoDateString(date: Date) {
    // Adjusting date to UTC to avoid timezone issues
    const adjustedDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes())
    );
    return adjustedDate.toISOString().split('T')[0];
}
